import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { motion } from "framer-motion";
import { Check, ArrowLeft, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { isPaidPlan } from "@/lib/trial";
import {
  PLANS,
  getCurrentPlanId,
  isUpgrade,
  type PlanId,
} from "@/lib/billing";
import { CancelPlanDialog } from "@/components/cancel-plan-dialog";
import { SwitchPlanDialog } from "@/components/switch-plan-dialog";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";
import { cancelScheduledChange } from "@/lib/billing-api";
import { useUpgradeCheckout } from "@/hooks/use-upgrade-checkout";
import { upgradePlanFromUrl } from "@/lib/upgrade-intent";
import { useSeo, SEO_PAGES } from "@/hooks/use-seo";

function friendlyDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

/**
 * Tints, not blocks. Each card is a near-white sheet with a hint of its plan
 * colour; Plus carries the only real accent so it reads as the hero.
 */
const CARD_STYLES: Record<PlanId, { background: string; borderColor: string }> = {
  starter: { background: "hsl(158 26% 97%)", borderColor: "hsl(158 16% 87%)" },
  plus: { background: "hsl(258 44% 96.5%)", borderColor: "hsl(258 35% 62%)" },
  premium: { background: "hsl(40 60% 96.5%)", borderColor: "hsl(40 28% 85%)" },
};

const FEATURED_PLAN: PlanId = "plus";

/** Shared CTA shape; the variant (Ink, white outline, or muted when disabled) comes from Button. */
const CTA_CLASS = "w-full px-5 rounded-[13px] text-sm gap-2";

export default function PricingScreen() {
  useSeo(SEO_PAGES["/pricing"]);
  const [, setLocation] = useLocation();
  const { user, isAuthenticated, refetch } = useAuth();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [switchTarget, setSwitchTarget] = useState<PlanId | null>(null);
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);
  const { checkoutEnabled, configLoaded, startingPlan, error: checkoutError, beginUpgrade } = useUpgradeCheckout();
  const [checkoutCanceled] = useState(
    () => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("checkout") === "canceled",
  );

  const currentPlanId = getCurrentPlanId(user);
  const onPaidPlan = isPaidPlan(user?.plan);
  const cancelsAtFriendly = friendlyDate(user?.planCancelsAt);
  const pendingPlanId = (user?.pendingPlan as PlanId | null | undefined) ?? null;
  const pendingPlanName = pendingPlanId ? PLANS.find((p) => p.id === pendingPlanId)?.name ?? null : null;
  const pendingEffectiveFriendly = friendlyDate(user?.pendingPlanEffectiveAt);

  // Coming back from sign-in after clicking "Upgrade" signed out (`?upgrade=plus`): pick
  // up where they left off and go straight to checkout — once, and only if it still applies.
  const resumedUpgrade = useRef(false);
  useEffect(() => {
    if (resumedUpgrade.current || !isAuthenticated || !configLoaded) return;
    const plan = upgradePlanFromUrl();
    if (!plan) return;
    resumedUpgrade.current = true;
    window.history.replaceState(window.history.state, "", window.location.pathname);
    if (checkoutEnabled && !onPaidPlan) void beginUpgrade(plan);
  }, [isAuthenticated, configLoaded, checkoutEnabled, onPaidPlan, beginUpgrade]);

  async function handleResume() {
    setResuming(true);
    setResumeError(null);
    try {
      const res = await authFetch(apiUrl("/billing/resume"), { method: "POST" });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Request failed (${res.status})`);
      }
      refetch({ silent: true });
    } catch (err) {
      setResumeError((err as Error).message ?? "Couldn't resume. Try again in a moment.");
    } finally {
      setResuming(false);
    }
  }

  async function handleUndoSwitch() {
    setUndoing(true);
    setUndoError(null);
    try {
      await cancelScheduledChange();
      refetch({ silent: true });
    } catch (err) {
      setUndoError((err as Error).message ?? "Couldn't undo. Try again in a moment.");
    } finally {
      setUndoing(false);
    }
  }

  return (
    <Layout hideNav>
      <div className="min-h-screen relative overflow-hidden">
        {/* Landing-hero washes (lavender centre, peach left, butter right). The utility is
            tuned for pages with the nav bar; this page has none, so lift it to match Figma. */}
        <div aria-hidden className="brand-washes pointer-events-none absolute inset-x-0 -top-[55px] h-[800px]" />

        <div className="max-w-4xl mx-auto px-5 py-12 relative z-10">
          {/* Back */}
          <button
            onClick={() => setLocation(isAuthenticated ? "/app" : "/")}
            className="flex items-center gap-1.5 text-sm text-muted-foreground mb-10 hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12 md:mb-16"
          >
            <h1 className="font-display text-4xl md:text-[3.25rem] font-semibold leading-[1.08] tracking-[-0.04em] text-foreground mb-3">
              Reflect at your own <span className="text-primary">rhythm</span>
            </h1>
            <p className="text-muted-foreground text-lg">
              Every plan includes the full Unfold experience.
            </p>
          </motion.div>

          {checkoutCanceled && !checkoutError && (
            <p className="mb-6 text-center text-sm text-muted-foreground">
              Checkout canceled — nothing was charged. Pick a plan whenever you&apos;re ready.
            </p>
          )}
          {checkoutError && (
            <p role="alert" className="mb-6 text-center text-sm text-red-700">
              {checkoutError}
            </p>
          )}
          {isAuthenticated && configLoaded && !checkoutEnabled && !onPaidPlan && (
            <p className="mb-6 text-center text-sm text-muted-foreground">
              New Plus and Premium upgrades are paused for a moment while we update billing. Starter is always free.
            </p>
          )}

          {/* Plan cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
            {PLANS.map((plan, idx) => {
              const isCurrent = plan.id === currentPlanId;
              const isFeatured = plan.id === FEATURED_PLAN;
              const isFree = plan.pricing == null;
              const features = plan.features;

              type Cta =
                | { kind: "disabled"; label: string }
                | { kind: "checkout"; label: string; plan: "plus" | "premium" }
                | { kind: "internal"; label: string }
                | { kind: "cancel"; label: string }
                | { kind: "resume"; label: string }
                | { kind: "switch"; label: string; targetPlan: PlanId };

              let cta: Cta;
              if (isCurrent) {
                cta = cancelsAtFriendly
                  ? { kind: "resume", label: "Resume my plan" }
                  : { kind: "disabled", label: "Your current plan" };
              } else if (isFree) {
                if (isAuthenticated && onPaidPlan) {
                  // User is on Plus / Premium — clicking "back to Starter" should
                  // walk them through cancellation feedback before flipping the plan.
                  cta = { kind: "cancel", label: "Continue on Starter" };
                } else {
                  cta = {
                    kind: "internal",
                    label: isAuthenticated ? "Continue on Starter" : "Start with Starter",
                  };
                }
              } else if (plan.id === pendingPlanId) {
                // Already scheduled to switch here — nothing more to do from this
                // card; "Undo" lives on the current-plan card instead.
                cta = { kind: "disabled", label: `Starts ${pendingEffectiveFriendly ?? "soon"}` };
              } else if (isAuthenticated && onPaidPlan) {
                // Existing paid subscriber switching tiers (either direction) —
                // never use the Payment Link (it would create a second
                // subscription and double-bill). Queue the switch instead; it
                // takes effect at the end of the current billing period.
                cta = {
                  kind: "switch",
                  label: isUpgrade(currentPlanId, plan.id) ? `Upgrade to ${plan.name}` : `Switch to ${plan.name}`,
                  targetPlan: plan.id,
                };
              } else if ((!isAuthenticated || checkoutEnabled) && (plan.id === "plus" || plan.id === "premium")) {
                // Signed out (e.g. on unfoldwin.com): always hand off to sign-in on the app
                // carrying this plan — whether checkout is open is decided per-user by the
                // server after sign-in, so a signed-out page can't know and mustn't block.
                // Signed in: only when the server says checkout is open for this user.
                cta = { kind: "checkout", label: `Upgrade to ${plan.name}`, plan: plan.id };
              } else {
                cta = { kind: "disabled", label: `Upgrade to ${plan.name}` };
              }

              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className={cn(
                    "relative flex flex-col rounded-[22px] px-6 md:px-7",
                    isFeatured
                      ? "py-6 md:py-8 md:-my-1.5 border-[1.1px] shadow-[0_12px_32px_-18px_hsl(258_40%_30%/0.5)]"
                      : "py-6 md:py-7 border-[0.6px] shadow-[0_2px_12px_-10px_hsl(24_20%_20%/0.4)]",
                  )}
                  style={CARD_STYLES[plan.id]}
                >
                  {isCurrent ? (
                    <div className="absolute top-[19px] right-4">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest",
                          cancelsAtFriendly
                            ? "bg-amber-500/90 text-white"
                            : "bg-foreground/85 text-background",
                        )}
                      >
                        {cancelsAtFriendly ? "Ending soon" : "Current"}
                      </span>
                    </div>
                  ) : isFeatured ? (
                    <div className="absolute top-[19px] right-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary text-white text-[10px] font-bold uppercase tracking-widest">
                        Most popular
                      </span>
                    </div>
                  ) : null}

                  <div className="mb-4">
                    <h2 className="font-display text-[1.375rem] font-medium leading-tight text-foreground">
                      {plan.name}
                    </h2>
                    <p className="mt-1 text-sm leading-snug text-foreground/55 md:min-h-[2.5rem]">
                      {plan.tagline}
                    </p>
                  </div>

                  {/* Monthly is the hero; the quarterly line states what actually
                      gets charged rather than offering a second option. */}
                  <div className="mb-5">
                    {plan.pricing ? (
                      <>
                        <div className="font-display text-[2.5rem] font-medium leading-none text-foreground">
                          ${plan.pricing.monthly}
                          <span className="font-sans text-sm font-normal text-foreground/50"> / month</span>
                        </div>
                        <div className="mt-2 text-xs text-foreground/45">
                          ${plan.pricing.quarterlyTotal} billed every 3 months
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-display text-[2.5rem] font-medium leading-none text-foreground">
                          Free
                        </div>
                        <div className="mt-2 text-xs text-foreground/45">Forever</div>
                      </>
                    )}
                  </div>

                  {/* The actual difference between the tiers: same product,
                      different reflection capacity. */}
                  <div className="mb-5 border-t-[0.6px] border-foreground/10 pt-4">
                    <p className="font-display text-2xl font-medium leading-none text-foreground">
                      {plan.unfoldingsPerMonth} unfoldings
                    </p>
                    <p className="mt-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-foreground/45">
                      per month
                    </p>
                  </div>

                  <div className="flex-1 mb-6">
                    {features.kind === "list" ? (
                      <>
                        <p className="text-sm font-medium text-foreground/80">{features.heading}</p>
                        <ul className="mt-2.5 space-y-2">
                          {features.items.map((feature) => (
                            <li key={feature} className="flex items-start gap-2 text-sm text-foreground/65">
                              <Check className="w-3.5 h-3.5 shrink-0 mt-[3px] text-foreground/35" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <p className="text-sm leading-relaxed text-foreground/70">{features.summary}</p>
                    )}
                  </div>

                  {isCurrent && cancelsAtFriendly && (
                    <p className="mb-3 text-xs font-medium text-amber-700/90 bg-amber-50/70 border border-amber-200/60 rounded-[13px] px-3 py-2 text-center">
                      Cancels on {cancelsAtFriendly} — full access until then.
                    </p>
                  )}

                  {isCurrent && !cancelsAtFriendly && pendingPlanName && pendingEffectiveFriendly && (
                    <p className="mb-3 text-xs font-medium text-foreground/70 bg-white/70 border border-foreground/10 rounded-[13px] px-3 py-2 text-center">
                      Switches to {pendingPlanName} on {pendingEffectiveFriendly}.{" "}
                      <button
                        type="button"
                        onClick={handleUndoSwitch}
                        disabled={undoing}
                        className="underline underline-offset-2 hover:text-foreground disabled:opacity-50"
                      >
                        {undoing ? "Undoing…" : "Undo"}
                      </button>
                    </p>
                  )}

                  {cta.kind === "checkout" ? (
                    <Button
                      type="button"
                      variant={isFeatured ? "default" : "outline"}
                      onClick={() => void beginUpgrade(cta.plan)}
                      disabled={startingPlan != null}
                      className={cn(CTA_CLASS, "disabled:opacity-70", isFeatured ? "h-11" : "h-[45px]")}
                    >
                      {startingPlan === cta.plan && <Loader2 className="w-4 h-4 animate-spin" />}
                      {startingPlan === cta.plan ? "Opening checkout…" : cta.label}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant={
                        cta.kind === "disabled"
                          ? "secondary"
                          : isFeatured || cta.kind === "resume"
                            ? "default"
                            : "outline"
                      }
                      disabled={cta.kind === "disabled" || (cta.kind === "resume" && resuming)}
                      onClick={() => {
                        if (cta.kind === "disabled") return;
                        if (cta.kind === "cancel") {
                          setCancelOpen(true);
                          return;
                        }
                        if (cta.kind === "switch") {
                          setSwitchTarget(cta.targetPlan);
                          return;
                        }
                        if (cta.kind === "resume") {
                          void handleResume();
                          return;
                        }
                        setLocation(isAuthenticated ? "/app" : "/login");
                      }}
                      className={cn(
                        CTA_CLASS,
                        cta.kind === "disabled"
                          ? "h-11 bg-foreground/5 text-foreground/40 disabled:opacity-100"
                          : isFeatured || cta.kind === "resume"
                            ? "h-11 disabled:opacity-70"
                            : "h-[45px]",
                      )}
                    >
                      {cta.kind === "resume" && resuming && <Loader2 className="w-4 h-4 animate-spin" />}
                      {cta.label}
                    </Button>
                  )}
                  {isCurrent && resumeError && (
                    <p className="mt-2 text-xs text-red-700 text-center">{resumeError}</p>
                  )}
                  {isCurrent && undoError && (
                    <p className="mt-2 text-xs text-red-700 text-center">{undoError}</p>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Guarantee */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center mt-10"
          >
            <p className="text-sm text-muted-foreground">
              {isAuthenticated && onPaidPlan ? (
                <button
                  type="button"
                  onClick={() => setCancelOpen(true)}
                  className="cursor-pointer underline decoration-from-font [text-underline-position:from-font] hover:text-foreground transition-colors"
                >
                  Change or cancel your plan
                </button>
              ) : (
                <span className="underline decoration-from-font [text-underline-position:from-font]">
                  Change or cancel your plan
                </span>
              )}
              {" "}anytime. Your journal stays yours.
            </p>
            <p className="mt-2 text-xs text-muted-foreground/75">
              Subscribe by August 31 and keep your early rate while your subscription remains active.
            </p>
          </motion.div>

          {/* FAQ */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {[
              {
                q: "What counts as an unfolding?",
                a: "Every time you paste a link, write a thought, or pick a trigger from the library and complete the reflection — that's one unfolding.",
              },
              {
                q: "Can I switch plans later?",
                a: "Yes. Upgrade or downgrade at any time. If you downgrade, you keep all your existing journals — you just get fewer unfoldings per month.",
              },
              {
                q: "What's in the library?",
                a: "Unfolding triggers — the resources and templates that kick off a reflection. Drop in a video or article, or use a ready-made template for moments like feeling stuck, low on energy, or craving clarity.",
              },
              {
                q: "Is my journal private?",
                a: "Yes. Your entries are stored securely and only visible to you. We never use your journal content to train AI models.",
              },
            ].map((faq) => (
              <div key={faq.q} className="surface-card rounded-[18px]! p-5">
                <p className="font-medium text-foreground text-sm mb-2">{faq.q}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
      <CancelPlanDialog open={cancelOpen} onOpenChange={setCancelOpen} />
      <SwitchPlanDialog
        targetPlanId={switchTarget}
        onOpenChange={(open) => {
          if (!open) setSwitchTarget(null);
        }}
      />
    </Layout>
  );
}
